/* ==========================================================================
   FLC Forge — Product Form & Variant Selects
   ========================================================================== */

class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.productHandle    = this.dataset.productHandle;
    this.productJSON      = JSON.parse(document.querySelector(`[data-product-json="${this.productHandle}"]`)?.textContent || '{}');
    this.inventoryData    = JSON.parse(document.querySelector(`[data-product-inventory="${this.productHandle}"]`)?.textContent || '{}');
    this.inputs        = this.querySelectorAll('select[name^="options"], input[name^="options"]');

    this.inputs.forEach(input => {
      input.addEventListener('change', () => this.updateVariant());
    });

    // Init from URL or first available
    this.setFromURL();
  }

  get currentOptions() {
    const seen = new Set();
    const result = [];
    for (const input of this.inputs) {
      if (input.type === 'radio') {
        if (seen.has(input.name)) continue;
        seen.add(input.name);
        const checked = this.querySelector(`input[name="${CSS.escape(input.name)}"]:checked`);
        if (checked) result.push(checked.value);
      } else {
        if (input.value) result.push(input.value);
      }
    }
    return result;
  }

  findVariant(options) {
    return this.productJSON.variants?.find(v =>
      v.options.every((opt, i) => opt === options[i])
    );
  }

  // Find the nearest valid variant by progressively relaxing options from the right.
  // e.g. ['Gray','Grey'] → tries ['Gray'] → returns first Gray variant (Gray/Black).
  _findBestVariant(options) {
    for (let len = options.length - 1; len >= 1; len--) {
      const v = this.productJSON.variants?.find(v =>
        options.slice(0, len).every((opt, i) => opt === v.options[i])
      );
      if (v) return v;
    }
    return this.productJSON.variants?.[0] || null;
  }

  // Silently update radio/select inputs to reflect a variant's options,
  // without dispatching change events (avoids triggering a second render).
  _applyVariantOptions(variant) {
    const seenNames = [];
    [...this.inputs].forEach(input => {
      if (!seenNames.includes(input.name)) seenNames.push(input.name);
    });
    seenNames.forEach((name, i) => {
      const val = variant.options[i];
      if (!val) return;
      const radio = this.querySelector(`input[type="radio"][name="${CSS.escape(name)}"][value="${CSS.escape(val)}"]`);
      if (radio) { radio.checked = true; return; }
      const select = this.querySelector(`select[name="${CSS.escape(name)}"]`);
      if (select) select.value = val;
    });
  }

  updateVariant() {
    const options = this.currentOptions;
    let variant = this.findVariant(options);

    // No exact match — find best partial match (left-to-right priority) and
    // update the DOM silently so we render with a valid variant from the start.
    if (!variant) {
      variant = this._findBestVariant(options);
      if (variant) this._applyVariantOptions(variant);
    }

    // Update hidden variant input immediately so the form is always correct
    document.querySelectorAll('[data-variant-id]').forEach(el => {
      el.value = variant?.id || '';
    });

    // Sync swatch option label text (e.g. "Colour: Teal Blue")
    this._updateOptionLabels(options);

    // Immediate in-memory feedback (fast — no network round-trip)
    this.updatePrices(variant);
    this.updateAvailability(variant);
    this.updateURL(variant);
    this.updateSelectedLabels(options);

    // Dispatch so ProductGallery navigates to the variant's featured image
    FLC.dispatch('flc:variant:changed', { variant, options }, this);

    // Section Rendering API: confirm prices, ATC state, and option availability
    // from fresh server HTML — same approach as Dawn's product-info.js
    this._renderFromSection(variant);
  }

  /**
   * Fetch the product section with the current variant and surgically update:
   *  - price wrap (respects Shopify scripts / metafield pricing)
   *  - ATC button disabled/text state (respects real-time inventory)
   *  - option button disabled states (reflects unavailable variant combos)
   *
   * In-flight requests are aborted when a new selection arrives so only the
   * latest variant's response is ever applied.
   */
  _renderFromSection(variant) {
    const sectionId = this.dataset.section;
    if (!sectionId || !this.productHandle) return;

    this._sectionAbort?.abort();
    this._sectionAbort = new AbortController();

    const variantParam = variant ? `&variant=${variant.id}` : '';
    const url = `/products/${this.productHandle}?section_id=${encodeURIComponent(sectionId)}${variantParam}`;

    fetch(url, { signal: this._sectionAbort.signal, headers: { Accept: 'text/html' } })
      .then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(text => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        this._swapPrices(html);
        this._swapButtonState(html, sectionId);
        this._swapOptionStates(html);
        this._swapIconTextMetafield(html);
        this._swapDeliveryInfo(html);
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.warn('VariantSelects: section render failed', err);
        }
      });
  }

  /** Update "OptionName: Value" spans for swatch option labels */
  _updateOptionLabels(options) {
    this.querySelectorAll('[data-option-index]').forEach(span => {
      const i = parseInt(span.dataset.optionIndex, 10);
      if (options[i] !== undefined) span.textContent = options[i];
    });
  }

  /** Replace the entire price block with the server-rendered version */
  _swapPrices(html) {
    const src = html.querySelector('[data-price-wrap]');
    const dst = document.querySelector('[data-price-wrap]');
    if (!src || !dst) return;
    dst.innerHTML = src.innerHTML;
    dst.className  = src.className;
  }

  /** Sync ATC button disabled state and button text from the server response */
  _swapButtonState(html, sectionId) {
    const srcBtn  = html.querySelector(`#ProductSubmitButton-${sectionId}`);
    const dstBtn  = document.querySelector(`#ProductSubmitButton-${sectionId}`);
    if (!srcBtn || !dstBtn) return;

    dstBtn.disabled = srcBtn.disabled;
    dstBtn.setAttribute('aria-disabled', srcBtn.disabled ? 'true' : 'false');

    const srcText = srcBtn.querySelector('[data-add-to-cart-text]');
    const dstText = dstBtn.querySelector('[data-add-to-cart-text]');
    if (srcText && dstText) dstText.innerHTML = srcText.innerHTML;

    // Keep price-in-button in sync (server has the correct formatted value)
    const srcPrice = srcBtn.querySelector('[data-add-to-cart-price]');
    const dstPrice = dstBtn.querySelector('[data-add-to-cart-price]');
    if (srcPrice && dstPrice) {
      dstPrice.innerHTML          = srcPrice.innerHTML;
      dstPrice.dataset.unitPrice  = srcPrice.dataset.unitPrice || '';
    }
  }

  /**
   * Sync option availability state from the server-rendered `variant-selects`
   * markup for the current selection. For radio inputs, this mirrors
   * `disabled`, `is-disabled`, `data-filtered`, and `is-filtered`. For select
   * dropdowns, this mirrors each option's `disabled` state.
   */
  _swapOptionStates(html) {
    const srcSelects = html.querySelector('variant-selects');
    if (!srcSelects) return;

    // Radio buttons
    srcSelects.querySelectorAll('input[type="radio"][name^="options"]').forEach(srcInput => {
      const dstInput = this.querySelector(
        `input[type="radio"][name="${CSS.escape(srcInput.name)}"][value="${CSS.escape(srcInput.value)}"]`
      );
      if (!dstInput) return;
      dstInput.disabled = srcInput.disabled;
      const isFiltered = srcInput.hasAttribute('data-filtered');
      dstInput.toggleAttribute('data-filtered', isFiltered);
      const srcLabel = srcSelects.querySelector(`label[for="${srcInput.id}"]`);
      const dstLabel = this.querySelector(`label[for="${dstInput.id}"]`);
      if (dstLabel) {
        dstLabel.classList.toggle('is-disabled', srcInput.disabled);
        dstLabel.classList.toggle('is-filtered', isFiltered);
        // Swap swatch image/color content so it reflects the current selection context
        const srcContent = srcLabel?.querySelector('.product__swatch-image, .product__swatch-color, .product__swatch-placeholder');
        const dstContent = dstLabel.querySelector('.product__swatch-image, .product__swatch-color, .product__swatch-placeholder');
        if (srcContent && dstContent) {
          dstContent.replaceWith(srcContent.cloneNode(true));
        }
      }
    });

    // Select dropdowns
    srcSelects.querySelectorAll('select[name^="options"]').forEach(srcSelect => {
      const dstSelect = this.querySelector(`select[name="${CSS.escape(srcSelect.name)}"]`);
      if (!dstSelect) return;
      srcSelect.querySelectorAll('option').forEach(srcOpt => {
        const dstOpt = dstSelect.querySelector(`option[value="${CSS.escape(srcOpt.value)}"]`);
        if (dstOpt) dstOpt.disabled = srcOpt.disabled;
      });
    });
  }

  _swapIconTextMetafield(html) {
    const src = html.querySelector('[data-icon-text-metafield]');
    const dst = document.querySelector('[data-icon-text-metafield]');
    if (!src || !dst) return;
    dst.innerHTML = src.innerHTML;
  }

  _swapDeliveryInfo(html) {
    const src = html.querySelector('[data-delivery-info]');
    const dst = document.querySelector('[data-delivery-info]');
    if (!src || !dst) return;
    dst.innerHTML = src.innerHTML;
  }

  updatePrices(variant) {
    const priceEl   = document.querySelector('[data-product-price]');
    const compareEl = document.querySelector('[data-product-compare-price]');
    const priceWrap = document.querySelector('[data-price-wrap]');
    const btnPrice  = document.querySelector('[data-add-to-cart-price]');

    if (!variant) return;

    if (priceEl)   priceEl.innerHTML   = FLC.formatMoneyNoCents(variant.price);
    if (compareEl) compareEl.innerHTML = variant.compare_at_price > variant.price
      ? FLC.formatMoneyNoCents(variant.compare_at_price)
      : '';

    if (btnPrice) {
      btnPrice.dataset.unitPrice = variant.price;
      const qty = parseInt(document.querySelector('[data-form-quantity]')?.value || '1', 10);
      btnPrice.textContent = FLC.formatMoneyNoCents(variant.price * qty);
    }

    priceWrap?.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
  }

  updateAvailability(variant) {
    const addBtn  = document.querySelector('[data-add-to-cart-btn]');
    const btnText = document.querySelector('[data-add-to-cart-text]');
    const btnPrice = document.querySelector('[data-add-to-cart-price]');

    if (!addBtn) return;

    const setPriceVisible = (visible) => {
      if (!btnPrice) return;
      btnPrice.style.display = visible ? '' : 'none';
    };

    if (!variant) {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
      if (btnText) btnText.textContent = window.flcForge.i18n?.unavailable || 'Unavailable';
      setPriceVisible(false);
    } else if (!variant.available) {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
      if (btnText) btnText.textContent = window.flcForge.i18n?.sold_out || 'Coming Soon';
      setPriceVisible(false);
    } else {
      addBtn.disabled = false;
      addBtn.setAttribute('aria-disabled', 'false');
      if (btnText) btnText.textContent = window.flcForge.i18n?.add_to_cart || 'Add to Cart';
      setPriceVisible(true);
    }

    // Keep product-form inventory data attributes in sync with the selected variant.
    // Use server-rendered inventory data — product JSON omits inventory_quantity.
    const inv = variant ? (this.inventoryData[variant.id] ?? {}) : {};
    const productForm = document.querySelector('product-form');
    if (productForm) {
      productForm.dataset.inventoryQty        = inv.inventory_quantity ?? '';
      productForm.dataset.inventoryManagement = inv.inventory_management ?? variant?.inventory_management ?? '';
      productForm.dataset.inventoryPolicy     = inv.inventory_policy    ?? variant?.inventory_policy    ?? '';
    }

    // Keep visible qty inputs capped at the variant's inventory limit
    const invMgmt   = inv.inventory_management ?? variant?.inventory_management;
    const invPolicy = inv.inventory_policy    ?? variant?.inventory_policy;
    const invQty    = inv.inventory_quantity;
    const maxQty = (invMgmt === 'shopify' && invPolicy === 'deny' && invQty != null)
      ? Math.max(0, invQty)
      : null;

    document.querySelectorAll('.product__qty-input').forEach(input => {
      if (maxQty !== null) {
        input.max = maxQty;
        const min = parseInt(input.min || '1', 10);
        if (parseInt(input.value, 10) > maxQty) {
          input.value = Math.max(min, maxQty);
          // Keep the hidden form qty input in sync when the value is clamped
          const syncTarget = input.dataset.qtySync ? document.getElementById(input.dataset.qtySync) : null;
          if (syncTarget) syncTarget.value = input.value;
        }
      } else {
        input.removeAttribute('max');
      }
    });
  }

  updateURL(variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Sync any [data-option-selected-value="<OptionName>"] elements with the
   * currently chosen value, so labels like "Colour: <Teal Blue>" stay live.
   */
  updateSelectedLabels(options) {
    const inputs = [...this.inputs];
    const seenNames = [];
    inputs.forEach((input) => {
      if (!seenNames.includes(input.name)) seenNames.push(input.name);
    });
    seenNames.forEach((name, idx) => {
      const value = options[idx];
      if (!value) return;
      const match = name.match(/^options\[(.+)\]$/);
      if (!match) return;
      const optionName = match[1];
      document
        .querySelectorAll(`[data-option-selected-value="${CSS.escape(optionName)}"]`)
        .forEach((el) => { el.textContent = value; });
    });
  }

  setFromURL() {
    const params  = new URLSearchParams(window.location.search);
    const varId   = params.get('variant');
    if (!varId) { this.updateVariant(); return; }

    const variant = this.productJSON.variants?.find(v => v.id == varId);
    if (!variant) { this.updateVariant(); return; }

    variant.options.forEach((opt, i) => {
      const input = this.inputs[i];
      if (!input) return;
      if (input.type === 'radio') {
        const radio = this.querySelector(`input[value="${CSS.escape(opt)}"]`);
        if (radio) radio.checked = true;
      } else {
        input.value = opt;
      }
    });

    this.updateVariant();
  }
}

customElements.define('variant-selects', VariantSelects);

/* Product Form (add to cart)
   ========================================================================== */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form       = this.querySelector('form');
    this.submitBtn  = this.querySelector('[data-add-to-cart-btn]');
    this.cartType   = window.flcForge.cartType || 'drawer';

    this.form?.addEventListener('submit', (e) => this.onSubmit(e));
  }

  _inventoryMax() {
    const mgmt   = this.dataset.inventoryManagement;
    const policy = this.dataset.inventoryPolicy;
    const qty    = this.dataset.inventoryQty;
    if (mgmt === 'shopify' && policy === 'deny' && qty !== '') {
      return parseInt(qty, 10);
    }
    return Infinity;
  }

  _resetQtyInputs(value) {
    document.querySelectorAll('.product__qty-input').forEach(input => {
      input.value = value;
      const syncTarget = input.dataset.qtySync ? document.getElementById(input.dataset.qtySync) : null;
      if (syncTarget) syncTarget.value = value;
    });
  }

  async onSubmit(e) {
    e.preventDefault();
    this.setLoading(true);

    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    if (!variantId) {
      this.setLoading(false);
      return;
    }

    // Read qty from the visible input (authoritative — what the user sees)
    const visibleQtyInput = this.closest('[data-section-id]')?.querySelector('.product__qty-input')
      ?? document.querySelector('.product__qty-input');
    const inputMax     = visibleQtyInput?.max ? parseInt(visibleQtyInput.max, 10) : Infinity;
    const requestedQty = visibleQtyInput
      ? (parseInt(visibleQtyInput.value, 10) || 1)
      : parseInt(formData.get('quantity') || '1', 10);


    // Keep hidden form input in sync before submit
    const formQtyInput = this.form?.querySelector('[name="quantity"]');
    if (formQtyInput && visibleQtyInput) formQtyInput.value = requestedQty;

    // Synchronous check: block immediately if visible qty already exceeds max
    if (inputMax !== Infinity && requestedQty > inputMax) {
      this._resetQtyInputs(inputMax);
      this.showError(`Only ${inputMax} ${inputMax === 1 ? 'item' : 'items'} available.`);
      this.setLoading(false);
      return;
    }

    // Async check: block if cart already holds stock that would push total over max
    const max = inputMax !== Infinity ? inputMax : this._inventoryMax();
    if (max !== Infinity) {
      try {
        const cart     = await FLC.fetchJSON('/cart.js');
        const cartItem = cart.items?.find(item => String(item.variant_id) === String(variantId));
        const cartQty  = cartItem ? cartItem.quantity : 0;
        const totalQty = cartQty + requestedQty;

        if (totalQty > max) {
          const canAdd = Math.max(0, max - cartQty);
          this._resetQtyInputs(Math.max(1, canAdd));
          if (canAdd === 0) {
            this.showError(`You already have all available stock (${max}) in your cart.`);
          } else {
            this.showError(`Only ${max} available — you have ${cartQty} in your cart already. You can add ${canAdd} more.`);
          }
          this.setLoading(false);
          return;
        }
      } catch (err) {
        console.error('Could not verify cart inventory:', err);
        this.showError('Unable to verify stock. Please try again.');
        this.setLoading(false);
        return;
      }
    }

    try {
      const res = await fetch(window.flcForge.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: requestedQty })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        this.showError(errData?.description || errData?.message || 'Not enough stock available.');
        return;
      }

      FLC.dispatch('flc:cart:refresh');

      if (this.cartType === 'drawer') {
        FLC.dispatch('flc:cart:open');
      } else if (this.cartType === 'page') {
        window.location.href = window.flcForge.routes.cart_url;
      }

      window.liveRegion?.announce(window.flcForge.i18n?.added_to_cart || 'Added to cart');
    } catch (err) {
      console.error('Add to cart failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  showError(msg) {
    const el = document.querySelector('[data-product-form-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(this._errorTimeout);
    this._errorTimeout = setTimeout(() => { el.hidden = true; }, 6000);
  }

  setLoading(state) {
    if (this.submitBtn) {
      this.submitBtn.disabled = state;
      this.submitBtn.setAttribute('aria-busy', state);
    }
  }
}

customElements.define('product-form', ProductForm);

/* Product Media Gallery
   ========================================================================== */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track       = this.querySelector('[data-gallery-track]');
    this.thumbBtns   = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.lineDots    = [...this.querySelectorAll('[data-gallery-dots] .product-gallery__line-dot')];
    this.prevBtn     = this.querySelector('[data-gallery-prev]');
    this.nextBtn     = this.querySelector('[data-gallery-next]');
    this.zoomOverlay = this.querySelector('[data-gallery-zoom]');
    this.zoomImg     = this.querySelector('[data-zoom-img]');
    this.prevZoomBtn = this.querySelector('[data-zoom-prev]');
    this.nextZoomBtn = this.querySelector('[data-zoom-next]');
    this.zoomSources = [...this.querySelectorAll('[data-zoom-src]')];

    this.currentIndex     = 0;
    this.currentZoomIndex = 0;
    this._zoomScale       = 1;
    this._zoomTx          = 0;
    this._zoomTy          = 0;
    this._isDragging      = false;
    this._wasDragging     = false;

    // Parse per-variant gallery image sets from embedded JSON
    this._variantGalleries = {};
    this.querySelectorAll('[data-variant-gallery]').forEach(el => {
      try { this._variantGalleries[el.dataset.variantGallery] = JSON.parse(el.textContent); }
      catch (_) {}
    });

    // Parse per-variant swatch images
    this._variantSwatches = {};
    this.querySelectorAll('[data-variant-swatch]').forEach(el => {
      try { this._variantSwatches[el.dataset.variantSwatch] = JSON.parse(el.textContent); }
      catch (_) {}
    });

    // Parse per-variant featured images (fallback when no image_gallery is set)
    this._variantFeaturedImages = {};
    this.querySelectorAll('[data-variant-featured-image]').forEach(el => {
      try { this._variantFeaturedImages[el.dataset.variantFeaturedImage] = JSON.parse(el.textContent); }
      catch (_) {}
    });

    this._lineLoader = this.querySelector('.gallery-line-loader') || null;

    // Snapshot default product-media slides so we can restore after a variant swap
    this._thumbsWrap      = this.querySelector('.product-gallery__thumbs') || null;
    this._dotsWrap        = this.querySelector('[data-gallery-dots]') || null;
    this._defaultTrackHTML = this.track ? this.track.innerHTML : '';
    this._defaultThumbsHTML = this._thumbsWrap ? this._thumbsWrap.innerHTML : '';
    this._defaultDotsHTML   = this._dotsWrap   ? this._dotsWrap.innerHTML   : '';

    // Thumbnail clicks
    this.thumbBtns.forEach(btn => {
      btn.addEventListener('click', () => this.goTo(parseInt(btn.dataset.galleryThumb, 10)));
    });

    // Gallery arrow clicks
    this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

    // Sync on manual swipe/scroll
    if (this.track) {
      this.track.addEventListener('scroll', FLC.debounce(() => this._syncFromScroll(), 80), { passive: true });
    }

    // Zoom setup
    if ('zoomEnabled' in this.dataset) {
      this._initZoom();
    }

    // Variant change → swap gallery in priority order:
    // 1. custom.image_gallery metafield images
    // 2. variant's own featured image
    // 3. fall back to main product gallery
    document.addEventListener('flc:variant:changed', e => {
      const variant = e.detail.variant;
      if (!variant) return;
      const id = String(variant.id);
      const galleryImages = this._variantGalleries[id];
      if (galleryImages && galleryImages.length) {
        this._applyVariantGallery(galleryImages);
      } else {
        const featuredImage = this._variantFeaturedImages[id];
        if (featuredImage) {
          this._applyVariantGallery([featuredImage]);
        } else {
          this._restoreDefaultGallery();
          const swatch = this._variantSwatches[id] || null;
          this._setSwatchSlide(swatch);
          if (variant.featured_media) {
            const mediaId = variant.featured_media.id;
            const slides  = [...this.querySelectorAll('[data-media-id]')];
            const idx     = slides.findIndex(el => el.dataset.mediaId == mediaId);
            if (idx !== -1) this.goTo(idx);
          }
        }
      }
    });

    this._syncArrows(0);

    // Show line loader while the very first gallery image loads on page load
    if (this._lineLoader && this.track) {
      const firstImg = this.track.querySelector('.product-gallery__img');
      if (firstImg && !firstImg.complete) {
        this._lineLoader.classList.add('is-active');
        const done = () => this._lineLoader.classList.remove('is-active');
        firstImg.addEventListener('load',  done, { once: true });
        firstImg.addEventListener('error', done, { once: true });
      }
    }

    // On initial page load, apply the selected variant's gallery immediately
    // so the PDP never shows the parent product gallery when a specific variant
    // is already selected (e.g. arriving via PLP swatch click with ?variant=...).
    const initialInput = document.querySelector('[data-variant-id]');
    const initialId    = initialInput ? String(initialInput.value) : null;
    if (initialId) {
      const galleryImages = this._variantGalleries[initialId];
      if (galleryImages && galleryImages.length) {
        this._applyVariantGallery(galleryImages);
      } else {
        const featuredImage = this._variantFeaturedImages[initialId];
        if (featuredImage) {
          this._applyVariantGallery([featuredImage]);
        } else {
          const swatch = this._variantSwatches[initialId] || null;
          if (swatch) this._setSwatchSlide(swatch);
        }
      }
    }
  }

  /* Variant gallery swap -------------------------------------------------- */
  _applyVariantGallery(images) {
    if (!this.track) return;
    // Swap shimmer takes over from here — clear any initial-load line loader
    if (this._lineLoader) this._lineLoader.classList.remove('is-active');
    this._swapGallery(() => {
      const mediaClasses = this.dataset.mediaClasses || 'media';
      const enableZoom   = 'zoomEnabled' in this.dataset;

      this.track.innerHTML = images.map((img, i) => {
        let inner;
        if (img.type === 'video') {
          const srcs = (img.sources || [])
            .filter(s => s.mimeType !== 'application/x-mpegURL')
            .sort((a, b) => (b.width || 0) - (a.width || 0))
            .map(s => `<source src="${s.url}" type="${s.mimeType}">`)
            .join('');
          const ratioStyle = (img.width && img.height) ? ` style="aspect-ratio: ${img.width} / ${img.height}"` : '';
          inner = `<div class="${mediaClasses} product-gallery__media--video"${ratioStyle}><video class="product-gallery__video" autoplay muted playsinline loop>${srcs}</video></div>`;
        } else {
          inner = `<div class="${mediaClasses}"${enableZoom ? ` data-zoom-src="${img.srcZoom}"` : ''}><img class="product-gallery__img" src="${img.src}" ${img.srcset ? `srcset="${img.srcset}" sizes="(min-width: 750px) 50vw, 100vw"` : ''} alt="${img.alt}" title="${img.alt}" loading="${i === 0 ? 'eager' : 'lazy'}" ${i === 0 ? 'fetchpriority="high"' : ''}></div>`;
        }
        return `<div class="product-gallery__slide" data-variant-slide>${inner}</div>`;
      }).join('');

      this.track.querySelectorAll('video').forEach(v => this._bindVideoEvents(v));

      const hasMultiple = images.length > 1;

      if (this._thumbsWrap) {
        this._thumbsWrap.innerHTML = images.map((img, i) => `
          <button
            class="product-gallery__thumb${i === 0 ? ' is-active' : ''}"
            data-gallery-thumb="${i}"
            aria-pressed="${i === 0}"
          >
            <img src="${img.thumb}" alt="${img.alt}" title="${img.alt}" loading="lazy">
          </button>`).join('');
      }

      if (this._dotsWrap) {
        this._dotsWrap.innerHTML = images.map((_, i) =>
          `<span class="product-gallery__line-dot${i === 0 ? ' is-active' : ''}"></span>`
        ).join('');
        this.lineDots = [...this._dotsWrap.querySelectorAll('.product-gallery__line-dot')];
        this._dotsWrap.style.display = hasMultiple ? '' : 'none';
      }

      this._rebindThumbs();
      if (enableZoom) this._rebindZoomSources();

      if (this.prevBtn) this.prevBtn.style.display = hasMultiple ? '' : 'none';
      if (this.nextBtn) this.nextBtn.style.display = hasMultiple ? '' : 'none';

      this._wrapSnapGroups();
      this.goTo(0);

    });
  }

  /* Wrap flat slides into snap-group + pair structure for grid_snap mode */
  _wrapSnapGroups() {
    if (!this._isVertical) return;
    const flatSlides = [...this.track.querySelectorAll(':scope > .product-gallery__slide')];
    if (!flatSlides.length) return;
    for (let gi = 0; gi < flatSlides.length; gi += 3) {
      const group = document.createElement('div');
      group.className = 'product-gallery__snap-group';
      group.appendChild(flatSlides[gi]);
      if (flatSlides[gi + 1]) {
        const pair = document.createElement('div');
        pair.className = 'product-gallery__pair';
        pair.appendChild(flatSlides[gi + 1]);
        if (flatSlides[gi + 2]) pair.appendChild(flatSlides[gi + 2]);
        group.appendChild(pair);
      }
      this.track.appendChild(group);
    }
  }

  _bindVideoEvents(video) {
    const mediaEl = video.parentElement;
    let shimmerStopped = false;

    const stopShimmer = () => {
      if (shimmerStopped) return;
      shimmerStopped = true;
      mediaEl?.classList.remove('product-gallery__media--video');
    };

    video.addEventListener('playing', stopShimmer);
    video.addEventListener('canplay', stopShimmer);

    video.play().catch(() => {});
  }

  _restoreDefaultGallery() {
    if (!this.track || this.track.innerHTML === this._defaultTrackHTML) return;
    this._swapGallery(() => {
      this.track.innerHTML = this._defaultTrackHTML;

      if (this._thumbsWrap) this._thumbsWrap.innerHTML = this._defaultThumbsHTML;

      if (this._dotsWrap) {
        this._dotsWrap.innerHTML     = this._defaultDotsHTML;
        this._dotsWrap.style.display = '';
        this.lineDots = [...this._dotsWrap.querySelectorAll('.product-gallery__line-dot')];
      }

      this._rebindThumbs();
      if ('zoomEnabled' in this.dataset) this._rebindZoomSources();

      if (this.prevBtn) this.prevBtn.style.display = '';
      if (this.nextBtn) this.nextBtn.style.display = '';

      // Re-bind autoplay, poster cover, and line loader for restored videos
      this.track.querySelectorAll('video').forEach(v => this._bindVideoEvents(v));
    });
  }

  _setSwatchSlide(swatch) {
    if (!this.track) return;
    const enableZoom = 'zoomEnabled' in this.dataset;

    // Remove any existing swatch slide
    this.track.querySelector('[data-swatch-slide]')?.remove();
    if (this._thumbsWrap) this._thumbsWrap.querySelector('[data-swatch-thumb]')?.remove();
    if (this._dotsWrap) this._dotsWrap.querySelector('[data-swatch-dot]')?.remove();

    if (!swatch) {
      this._rebindThumbs();
      if (enableZoom) this._rebindZoomSources();
      this._syncArrows(this.currentIndex);
      if (this._dotsWrap) this.lineDots = [...this._dotsWrap.querySelectorAll('.product-gallery__line-dot')];
      return;
    }

    const mediaClasses = this.dataset.mediaClasses || 'media';
    const slideIdx     = this.track.children.length;

    const slide = document.createElement('div');
    slide.className = 'product-gallery__slide';
    slide.dataset.swatchSlide = '';
    slide.innerHTML = `
      <div class="${mediaClasses}"${enableZoom ? ` data-zoom-src="${swatch.srcZoom}"` : ''}>
        <img class="product-gallery__img" src="${swatch.src}" alt="${swatch.alt}" title="${swatch.alt}" loading="lazy">
      </div>`;
    this.track.appendChild(slide);

    if (this._thumbsWrap) {
      const btn = document.createElement('button');
      btn.className = 'product-gallery__thumb';
      btn.dataset.galleryThumb = String(slideIdx);
      btn.dataset.swatchThumb  = '';
      btn.setAttribute('aria-pressed', 'false');
      btn.innerHTML = `<img src="${swatch.thumb}" alt="${swatch.alt}" title="${swatch.alt}" loading="lazy">`;
      this._thumbsWrap.appendChild(btn);
    }

    if (this._dotsWrap) {
      const dot = document.createElement('span');
      dot.className = 'product-gallery__line-dot';
      dot.dataset.swatchDot = '';
      this._dotsWrap.appendChild(dot);
      this.lineDots = [...this._dotsWrap.querySelectorAll('.product-gallery__line-dot')];
      this._dotsWrap.style.display = '';
    }

    if (this.prevBtn) this.prevBtn.style.display = '';
    if (this.nextBtn) this.nextBtn.style.display = '';

    this._rebindThumbs();
    if (enableZoom) this._rebindZoomSources();
    this._syncArrows(this.currentIndex);
  }

  _swapGallery(fn) {
    const wrap = this.querySelector('.product-gallery__track-wrap') || this.track;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { fn(); return; }
    wrap.classList.add('product-gallery--swapping');
    let done = false;
    const onEnd = () => {
      if (done) return;
      done = true;
      wrap.removeEventListener('transitionend', onEnd);
      fn();
      // Removing the class triggers the fade-in via the base transition
      requestAnimationFrame(() => wrap.classList.remove('product-gallery--swapping'));
    };
    wrap.addEventListener('transitionend', onEnd, { once: true });
    setTimeout(onEnd, 250);
  }

  _rebindThumbs() {
    this.thumbBtns = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.thumbBtns.forEach(btn => {
      btn.addEventListener('click', () => this.goTo(parseInt(btn.dataset.galleryThumb, 10)));
    });
  }

  _rebindZoomSources() {
    this.zoomSources = [...this.querySelectorAll('[data-zoom-src]')];
    this.zoomSources.forEach((el, i) => {
      el.addEventListener('click', () => {
        if (!this._wasDragging) this._openZoom(i);
      });
    });
  }

  /* Gallery navigation ---------------------------------------------------- */
  get _slideCount() {
    return this.track ? this.track.querySelectorAll('.product-gallery__slide').length : 0;
  }

  get _isVertical() {
    return this.dataset.galleryType === 'grid_snap' && window.innerWidth >= 750;
  }

  goTo(index) {
    if (!this.track) return;
    const i   = Math.max(0, Math.min(index, this._slideCount - 1));
    const isV = this._isVertical;
    if (isV) {
      const slide = this.track.children[i];
      if (slide) {
        this.track.scrollTo({ top: slide.offsetTop, behavior: 'smooth' });
      }
    } else {
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' });
    }
    this.currentIndex = i;
    this._syncThumbs(i);
    this._syncArrows(i);
    this._syncDots(i);
  }

  _syncFromScroll() {
    if (!this.track) return;
    const isV = this._isVertical;
    let i;
    if (isV) {
      const scrollTop = this.track.scrollTop;
      const slides    = [...this.track.children];
      let minDist = Infinity;
      i = 0;
      slides.forEach((slide, j) => {
        const dist = Math.abs(slide.offsetTop - scrollTop);
        if (dist < minDist) { minDist = dist; i = j; }
      });
    } else {
      const size = this.track.clientWidth;
      if (!size) return;
      i = Math.round(this.track.scrollLeft / size);
    }
    if (i === this.currentIndex) return;
    this.currentIndex = i;
    this._syncThumbs(i);
    this._syncArrows(i);
    this._syncDots(i);
  }

  _syncThumbs(i) {
    this.thumbBtns.forEach((btn, j) => {
      btn.classList.toggle('is-active', j === i);
      btn.setAttribute('aria-pressed', String(j === i));
    });
  }

  _syncArrows(i) {
    if (this.prevBtn) this.prevBtn.disabled = i === 0;
    if (this.nextBtn) this.nextBtn.disabled = i >= this._slideCount - 1;
  }

  _syncDots(i) {
    this.lineDots.forEach((dot, j) => dot.classList.toggle('is-active', j === i));
  }

  /* Zoom ------------------------------------------------------------------ */
  _initZoom() {
    // Open zoom by clicking any zoomable media
    this.zoomSources.forEach((el, i) => {
      el.addEventListener('click', () => {
        if (!this._wasDragging) this._openZoom(i);
      });
    });

    // Close
    this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this._closeZoom());
    this.zoomOverlay?.addEventListener('click', e => {
      if (e.target === this.zoomOverlay) this._closeZoom();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.zoomOverlay?.getAttribute('aria-hidden') === 'false') this._closeZoom();
      if (e.key === 'ArrowLeft'  && this.zoomOverlay?.getAttribute('aria-hidden') === 'false') this._stepZoom(-1);
      if (e.key === 'ArrowRight' && this.zoomOverlay?.getAttribute('aria-hidden') === 'false') this._stepZoom(1);
    });

    // Zoom carousel arrows
    this.prevZoomBtn?.addEventListener('click', () => this._stepZoom(-1));
    this.nextZoomBtn?.addEventListener('click', () => this._stepZoom(1));

    // Click image to toggle zoom scale
    this.zoomImg?.addEventListener('click', e => {
      if (this._wasDragging) { this._wasDragging = false; return; }
      this._toggleScale(e);
    });

    // Drag to pan
    this._initDrag();
  }

  _openZoom(index) {
    const src = this.zoomSources[index];
    if (!src || !this.zoomImg) return;
    this._resetTransform();
    this.zoomImg.src    = src.dataset.zoomSrc;
    this.zoomImg.alt    = src.querySelector('img')?.alt || '';
    this.zoomImg.title  = this.zoomImg.alt;
    this.currentZoomIndex = index;
    this.zoomOverlay.setAttribute('aria-hidden', 'false');
    this._syncZoomNav(index);
    FLC.openOverlay();
  }

  _closeZoom() {
    if (!this.zoomOverlay) return;
    this._resetTransform();
    this.zoomOverlay.setAttribute('aria-hidden', 'true');
    this.zoomImg.src = '';
    FLC.closeOverlay();
  }

  _stepZoom(dir) {
    const next = Math.max(0, Math.min(this.zoomSources.length - 1, this.currentZoomIndex + dir));
    if (next === this.currentZoomIndex) return;
    const src = this.zoomSources[next];
    this._resetTransform();
    this.zoomImg.src    = src.dataset.zoomSrc;
    this.zoomImg.alt    = src.querySelector('img')?.alt || '';
    this.zoomImg.title  = this.zoomImg.alt;
    this.currentZoomIndex = next;
    this._syncZoomNav(next);
  }

  _syncZoomNav(i) {
    const total = this.zoomSources.length;
    if (total <= 1) { return; } // buttons are already hidden via HTML [hidden]
    this.prevZoomBtn?.removeAttribute('hidden');
    this.nextZoomBtn?.removeAttribute('hidden');
    if (this.prevZoomBtn) this.prevZoomBtn.disabled = i === 0;
    if (this.nextZoomBtn) this.nextZoomBtn.disabled = i >= total - 1;
  }

  /* Scale toggle (click to zoom in/out) ----------------------------------- */
  _toggleScale(e) {
    if (this._zoomScale > 1) {
      this._resetTransform();
      return;
    }
    const rect  = this.zoomImg.getBoundingClientRect();
    const s     = 2.5;
    // Keep the clicked point fixed: tx = (clickOffset - center) * (1 - s)
    this._zoomScale = s;
    this._zoomTx    = (e.clientX - rect.left - rect.width  / 2) * (1 - s);
    this._zoomTy    = (e.clientY - rect.top  - rect.height / 2) * (1 - s);
    this._applyTransform();
    this.zoomImg.style.cursor = 'grab';
  }

  _resetTransform() {
    this._zoomScale = 1;
    this._zoomTx    = 0;
    this._zoomTy    = 0;
    if (this.zoomImg) {
      this.zoomImg.style.transform = '';
      this.zoomImg.style.cursor    = '';
    }
  }

  _applyTransform() {
    if (this.zoomImg) {
      this.zoomImg.style.transform = `translate(${this._zoomTx}px,${this._zoomTy}px) scale(${this._zoomScale})`;
    }
  }

  /* Drag to pan ----------------------------------------------------------- */
  _initDrag() {
    const onStart = (cx, cy) => {
      if (this._zoomScale <= 1) return;
      this._isDragging  = true;
      this._wasDragging = false;
      this._dragX = cx - this._zoomTx;
      this._dragY = cy - this._zoomTy;
      if (this.zoomImg) this.zoomImg.style.cursor = 'grabbing';
    };

    const onMove = (cx, cy) => {
      if (!this._isDragging) return;
      this._wasDragging = true;
      this._zoomTx = cx - this._dragX;
      this._zoomTy = cy - this._dragY;
      this._applyTransform();
    };

    const onEnd = () => {
      if (!this._isDragging) return;
      this._isDragging = false;
      if (this.zoomImg) this.zoomImg.style.cursor = this._zoomScale > 1 ? 'grab' : '';
    };

    // Mouse
    this.zoomImg?.addEventListener('mousedown', e => { onStart(e.clientX, e.clientY); e.preventDefault(); });
    window.addEventListener('mousemove', e => onMove(e.clientX, e.clientY));
    window.addEventListener('mouseup', onEnd);

    // Touch
    this.zoomImg?.addEventListener('touchstart', e => {
      if (e.touches.length === 1) onStart(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    window.addEventListener('touchmove', e => {
      if (e.touches.length === 1) onMove(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    window.addEventListener('touchend', onEnd);
  }
}

customElements.define('product-gallery', ProductGallery);

/* Quantity Steppers
   ========================================================================== */
(function initQuantitySteppers() {
  function updateBtnPrice(qty) {
    const btnPrice = document.querySelector('[data-add-to-cart-price]');
    if (!btnPrice) return;
    const unitPrice = parseInt(btnPrice.dataset.unitPrice || '0', 10);
    if (!unitPrice) return;
    btnPrice.textContent = FLC.formatMoneyNoCents(unitPrice * qty);
  }

  function syncQty(input) {
    const targetId = input.dataset.qtySync;
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (target) target.value = input.value;
    updateBtnPrice(parseInt(input.value || '1', 10));
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quantity-change]');
    if (!btn) return;
    const wrap  = btn.closest('.product__qty-wrap');
    const input = wrap?.querySelector('input[type="number"]');
    if (!input) return;
    const delta    = parseInt(btn.dataset.quantityChange, 10);
    const min      = parseInt(input.min || '1', 10);
    const max      = input.max ? parseInt(input.max, 10) : Infinity;
    const requested = Math.max(min, parseInt(input.value || '1', 10) + delta);
    if (delta > 0 && requested > max) {
      const errEl = document.querySelector('[data-product-form-error]');
      if (errEl) {
        errEl.textContent = `Only ${max} ${max === 1 ? 'item' : 'items'} available.`;
        errEl.hidden = false;
        clearTimeout(errEl._hideTimer);
        errEl._hideTimer = setTimeout(() => { errEl.hidden = true; }, 6000);
      }
      return;
    }
    input.value = requested;
    syncQty(input);
  });

  document.addEventListener('change', (e) => {
    if (!('qtySync' in e.target.dataset)) return;
    const input = e.target;
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    const min = parseInt(input.min || '1', 10);
    if (parseInt(input.value, 10) > max) {
      input.value = max;
      const errEl = document.querySelector('[data-product-form-error]');
      if (errEl) {
        errEl.textContent = `Only ${max} ${max === 1 ? 'item' : 'items'} available.`;
        errEl.hidden = false;
        clearTimeout(errEl._hideTimer);
        errEl._hideTimer = setTimeout(() => { errEl.hidden = true; }, 6000);
      }
    }
    syncQty(input);
  });
})();
